<template>
  <div>
    <header class="vtm-header">
      <button
        class="vtm-header__iconbtn"
        type="button"
        :title="collapse ? $t('header.open') : $t('header.close')"
        :aria-label="collapse ? $t('header.open') : $t('header.close')"
        @click="isHeader"
      >
        <i :class="collapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
      </button>

      <div class="vtm-header__title">{{ pageTitle }}</div>
      <span class="vtm-beta-badge">{{ $t("beta.badge") }}</span>

      <div class="vtm-header__spacer"></div>

      <!-- 通知中心（常驻） -->
      <notification-center></notification-center>

      <!-- 主题切换 -->
      <button
        class="vtm-header__iconbtn"
        type="button"
        :title="isDark ? $t('header.themeLight') : $t('header.themeDark')"
        :aria-label="isDark ? $t('header.themeLight') : $t('header.themeDark')"
        @click="toggleTheme"
      >
        <i :class="isDark ? 'el-icon-sunny' : 'el-icon-moon'"></i>
      </button>

      <!-- 全屏 -->
      <button class="vtm-header__iconbtn" type="button" :title="$t('header.screen')" :aria-label="$t('header.screen')" @click="screen">
        <i class="el-icon-full-screen"></i>
      </button>

      <!-- 用户 -->
      <el-dropdown trigger="click" @command="userCommand">
        <button class="vtm-header__user" type="button">
          <el-avatar class="vtm-header__avatar" :size="28" :src="userAvatar">
            {{ (nickName || "?").charAt(0).toUpperCase() }}
          </el-avatar>
          <span class="vtm-header__name">{{ nickName }}</span>
          <i class="el-icon-arrow-down vtm-header__caret"></i>
        </button>
        <el-dropdown-menu slot="dropdown" class="vtm-account-menu">
          <div v-if="canWallet" class="vtm-account-menu__balance">
            <span class="vtm-account-menu__balance-label">{{ $t("header.walletBalance") }}</span>
            <span class="vtm-account-menu__balance-val vtm-num">{{ $currency(walletBalance) }}</span>
            <el-button size="mini" type="primary" plain class="vtm-account-menu__topup" @click="userCommand('wallet')">
              {{ $t("header.topUp") }}
            </el-button>
          </div>
          <el-dropdown-item command="subscription" icon="el-icon-bank-card">{{ $t("header.manageSubscriptions") }}</el-dropdown-item>
          <el-dropdown-item command="account" icon="el-icon-user">{{ $t("header.accountSettings") }}</el-dropdown-item>
          <el-dropdown-item command="notifications" icon="el-icon-bell">{{ $t("header.notificationSettings") }}</el-dropdown-item>
          <el-dropdown-item command="classic" icon="el-icon-back">{{ $t("beta.switchClassic") }}</el-dropdown-item>
          <el-dropdown-item command="password" divided icon="el-icon-key">{{ $t("header.password") }}</el-dropdown-item>
          <el-dropdown-item command="logout" icon="el-icon-switch-button">{{ $t("header.loginOut") }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </header>

    <!-- 设备过期提醒对话框 -->
    <el-dialog
      :title="$t('header.alertExpiration')"
      :visible.sync="expireDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="true"
      append-to-body
      :modal="true"
    >
      <!-- 提醒信息（拆分为标题与描述两行，避免英文长句断词） -->
      <el-alert
        :title="expireMessageTitle"
        :description="expireMessageDesc"
        type="warning"
        :closable="false"
        show-icon
        class="custom-expire-alert"
        :style="{ marginBottom: '20px', wordBreak: 'normal' }"
      >
      </el-alert>
      <!-- 分成两个列表：上方新设备，下方旧设备；isTransferNew == 1 视为新设备，== 0 视为旧设备 -->
      <div v-if="noticeDevices.length > 0">
        <!-- 新设备列表 -->
        <div v-if="newDevices.length > 0" style="margin-bottom: 16px">
          <el-divider content-position="left">
            <el-tag type="success" size="medium">Number of Devices ({{ newDevices.length }})</el-tag>
          </el-divider>
          <el-table :data="newDevices" border size="small" max-height="300" @selection-change="onNewDeviceSelectionChange">
            <el-table-column type="selection" width="45"></el-table-column>
            <el-table-column prop="equipmentNo" :label="$t('header.deviceNo')" min-width="140"></el-table-column>
            <el-table-column prop="equipmentName" :label="$t('header.deviceName')" min-width="120"></el-table-column>
            <el-table-column prop="isTransferNew" :label="$t('header.deviceAge')" min-width="100">
              <template slot-scope="scope">
                <el-tag :type="getDeviceStatusType(scope.row)" size="mini">{{ getDeviceStatusText(scope.row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="expireDate" :label="$t('header.expireTime')" min-width="160">
              <template slot-scope="scope">
                <el-tag type="warning" size="mini">{{ formatExpireDate(scope.row.expireDate) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 旧设备列表 -->
        <div v-if="oldDevices.length > 0">
          <el-divider content-position="left">
            <el-tag type="info" size="medium">Number of Devices ({{ oldDevices.length }})</el-tag>
          </el-divider>
          <el-table :data="oldDevices" border size="small" max-height="300" @selection-change="onOldDeviceSelectionChange">
            <el-table-column type="selection" width="45"></el-table-column>
            <el-table-column prop="equipmentNo" :label="$t('header.deviceNo')" min-width="140"></el-table-column>
            <el-table-column prop="equipmentName" :label="$t('header.deviceName')" min-width="120"></el-table-column>
            <el-table-column prop="isTransferNew" :label="$t('header.deviceAge')" min-width="100">
              <template slot-scope="scope">
                <el-tag :type="getDeviceStatusType(scope.row)" size="mini">{{ getDeviceStatusText(scope.row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="expireDate" :label="$t('header.expireTime')" min-width="160">
              <template slot-scope="scope">
                <el-tag type="warning" size="mini">{{ formatExpireDate(scope.row.expireDate) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button type="primary" :disabled="selectedDevicesCount === 0" @click="batchRenewal">
          {{ $t("header.renewAction") }}
          <span v-if="selectedDevicesCount > 0">({{ selectedDevicesCount }})</span>
        </el-button>
      </span>
    </el-dialog>
    
    <!-- Welcome Guide Dialog -->
    <el-dialog
      :visible.sync="showWelcomeDialog"
      width="650px"
      :modal-append-to-body="true"
      :append-to-body="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="true"
    >
      <div style="padding:24px;">
        <h3 class="vtm-welcome-sub__title" style="font-size:20px;margin:0 0 16px 0;font-weight:600;word-break:keep-all;">Welcome! Let's get you set up with a subscription:</h3>
        
        <p class="vtm-welcome-sub__text" style="margin:0 0 16px 0;font-size:15px;line-height:1.6;word-break:keep-all;">To keep things quick and secure, we use Stripe to manage all payments:</p>
        
        <div class="vtm-welcome-sub__step" style="border-radius:8px;padding:20px;margin-bottom:16px;">
          <h4 class="vtm-welcome-sub__title" style="margin:0 0 12px 0;font-size:16px;font-weight:600;word-break:keep-all;">1. First, add your payment method.</h4>
          <p class="vtm-welcome-sub__text" style="margin:0;font-size:14px;line-height:1.6;word-break:keep-all;">Click the button below to securely add your credit/debit card (or other payment method) in Stripe's window.</p>
        </div>
        
        <div class="vtm-welcome-sub__step" style="border-radius:8px;padding:20px;">
          <h4 class="vtm-welcome-sub__title" style="margin:0 0 12px 0;font-size:16px;font-weight:600;word-break:keep-all;">2. Come back here and choose your plan.</h4>
          <p class="vtm-welcome-sub__text" style="margin:0;font-size:14px;line-height:1.6;word-break:keep-all;">Once your payment method is added, return to this page and pick the plan that's right for you, then click confirm.</p>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer" style="text-align:center;display:flex;justify-content:center;">
        <el-button type="primary" style="min-width:240px;height:44px;font-size:16px;" @click="redirectToStripePortal">
          ADD Payment Method
        </el-button>
      </span>
    </el-dialog>

    <machine-renewal
      :nickName="nickName"
      :renewalList="renewalList"
      v-on:cancelRenewal="cancelRenewal"
      :dialogBatchRenewal="dialogBatchRenewal"
    />

    <notification-settings :visible.sync="notifSettingsVisible"></notification-settings>
    <account-settings :visible.sync="accountSettingsVisible"></account-settings>
  </div>
</template>

<script>
  import screenfull from "screenfull";
  import config from "@/http/config";
  import eventBus from "@/utils/eventBus";
  import machineRenewal from "_c/machine/user/MachineRenewal";
  import notificationCenter from "_c/NotificationCenter";
  import notificationSettings from "_c/account/NotificationSettings";
  import accountSettings from "_c/account/AccountSettings";
  import { THEMES } from "@/utils/theme";
  import { grantedLinks } from "@/utils/navSections";

  export default {
    name: "sys-header",
    components: {
      machineRenewal,
      notificationCenter,
      notificationSettings,
      accountSettings
    },
    data() {
      return {
        walletBalance: null,
        dialogPassword: false,
        drawer: false,
        notifSettingsVisible: false,
        accountSettingsVisible: false,
        config: config,
        expireDialogVisible: false,
        // 订阅成功时允许关闭头部过期提醒弹窗
        allowExpireDialogClose: false,
        expiredDevices: [],
        aboutExpireDevices: [],
        noticeDevices: [],
        newDevices: [],
        oldDevices: [],
        expireMessage: "",
        expireMessageTitle: "",
        expireMessageDesc: "",
        dialogBatchRenewal: false,
        renewalList: [],
        selectedExpiredDevices: [],
        selectedAboutExpireDevices: [],
        selectedNewDevices: [],
        selectedOldDevices: [],
        showWelcomeDialog: false, // Show welcome guide dialog
        hasPaymentMethod: true // Track if user has payment method
      };
    },
    mounted() {
      let avatar = this.$cookies.get("avatar");
      let nickName = this.$cookies.get("nickName");
      this.$store.dispatch("updateAvatar", avatar);
      this.$store.dispatch("updateUser", { nickName: nickName });
      this.loadWalletBalance();
      // 登录后拉取过期信息：若需要先显示隐私协议，则延后到用户同意后再拉取
      const needPrivacyFirst = String(this.$route.query.showAgreePrivacy) === '0' || this.$store.state.privacyVisible === true;
      if (needPrivacyFirst) {
        const onPrivacyAccepted = () => {
          eventBus.off("openPasswordDialog", onPrivacyAccepted);
          this.getMachineInfo().catch(() => {
            // 接口异常不弹窗
          });
        };
        eventBus.on("openPasswordDialog", onPrivacyAccepted);
      } else {
        this.getMachineInfo().catch(() => {
          // 接口异常不弹窗
        });
      }
      eventBus.on("openPasswordDialog", this.handlePassword);
    },
    methods: {
      // 判断是否为超级管理员：参考 Role.vue 逻辑
      // Role.vue 中通过 sessionStorage.getItem('parentId') 判断：
      // parseInt(parentId) > 0 表示非管理员；否则为管理员（含未设置/NaN）。
      isSuperAdmin() {
        const pid = sessionStorage.getItem("parentId");
        const n = parseInt(pid, 10);
        return pid === null || isNaN(n) || n <= 0;
      },
      // 管理你的订阅 等待接口
      async manageSubscription() {
        try {
          const response = await this.$api.request(this.$url.Stripe.machineGetUrl, this.$method.get);

          if (response.code === 0 && response.data) {
            // 在当前窗口跳转到返回的URL
            window.location.href = response.data;
          } else {
            this.$message.error(response.msg || this.$t("header.failedGetSubscriptionURL"));
          }
        } catch (error) {
          console.error("Error getting subscription URL:", error);
          this.$message.error(this.$t("header.failedGetSubscriptionURL"));
        }
      },
      // 订阅组件回调：dialog 表示是否继续显示订阅弹窗；state 为订阅结果（true 表示订阅成功）
      cancelRenewal(dialog, state) {
        // 关闭 MachineRenewal 弹窗
        this.dialogBatchRenewal = dialog;
        // 订阅成功后，允许并关闭头部的过期提醒弹窗
        if (state) {
          this.allowExpireDialogClose = true;
          this.expireDialogVisible = false;
        }
      },
      getMachineInfo() {
        // 改为使用新接口：system/getEquipmentExpired（this.$url.Index.machineInfo）
        // 后端可能返回：
        // - expiredDevices / aboutExpireDevices（设备数组）
        // - aboutDevices（即将过期别名）
        // - expiredCount / aboutCount（数量）
        // - expired / about（数量别名）
        // - unsubscribedDevices（未订阅设备数组）
        return this.$api.request(this.$url.Index.machineInfo, this.$method.get).then(res => {
          if (res.code === 0 && res.data) {
            const data = res.data || {};

            // 统一设备列表字段
            const expiredList = Array.isArray(data.expiredDevices)
              ? data.expiredDevices
              : Array.isArray(data.expired)
              ? data.expired
              : Array.isArray(data.expiredCount)
              ? data.expiredCount
              : [];
            const aboutList = Array.isArray(data.aboutExpireDevices)
              ? data.aboutExpireDevices
              : Array.isArray(data.aboutDevices)
              ? data.aboutDevices
              : Array.isArray(data.aboutCount)
              ? data.aboutCount
              : Array.isArray(data.about)
              ? data.about
              : [];
            // 检查未订阅设备（设备没有 stripeSubscriptionId）
            const unsubscribedList = Array.isArray(data.unsubscribedDevices)
              ? data.unsubscribedDevices
              : Array.isArray(data.unsubscribed)
              ? data.unsubscribed
              : [];

            // 计算数量，兼容仅返回数量的情况
            const expiredNum = Array.isArray(expiredList)
              ? expiredList.length
              : typeof data.expiredCount === "number"
              ? data.expiredCount
              : typeof data.expired === "number"
              ? data.expired
              : 0;
            const aboutNum = Array.isArray(aboutList)
              ? aboutList.length
              : typeof data.aboutCount === "number"
              ? data.aboutCount
              : typeof data.about === "number"
              ? data.about
              : 0;
            const unsubscribedNum = Array.isArray(unsubscribedList)
              ? unsubscribedList.length
              : typeof data.unsubscribedCount === "number"
              ? data.unsubscribedCount
              : 0;

            // 更新 Store（用于右上角消息提示与红点）
            this.$store.dispatch("machineInfo", {
              expiredCount: expiredNum,
              aboutCount: aboutNum
            });

            // 如果API没有返回未订阅设备列表，尝试从返回的设备列表中筛选未订阅的设备
            if (unsubscribedList.length === 0) {
              // 合并所有设备列表，筛选出没有 stripeSubscriptionId 的设备
              const allDevicesFromResponse = [
                ...expiredList,
                ...aboutList,
                ...(Array.isArray(data.list) ? data.list : []),
                ...(Array.isArray(data.devices) ? data.devices : []),
                ...(Array.isArray(data.equipmentList) ? data.equipmentList : [])
              ];
              
              // 去重并筛选未订阅设备
              const deviceMap = new Map();
              allDevicesFromResponse.forEach(device => {
                if (device && device.equipmentNo) {
                  const key = device.equipmentNo;
                  if (!deviceMap.has(key)) {
                    deviceMap.set(key, device);
                  }
                }
              });
              
              // 筛选出没有 stripeSubscriptionId 的设备
              const filteredUnsubscribed = Array.from(deviceMap.values()).filter(
                device => !device.stripeSubscriptionId || device.stripeSubscriptionId === '' || device.stripeSubscriptionId === null
              );
              
              if (filteredUnsubscribed.length > 0) {
                // 将未订阅设备添加到数据中
                data.unsubscribedDevices = filteredUnsubscribed;
              }
            }

            // 如果仍然没有未订阅设备且没有过期/即将过期设备，尝试单独获取用户设备列表来检查
            if ((!data.unsubscribedDevices || data.unsubscribedDevices.length === 0) && 
                expiredNum === 0 && aboutNum === 0) {
              // 获取用户设备列表，检查是否有未订阅的设备
              this.$api.request(this.$url.UserMachine.userList, this.$method.get, {}).then(userRes => {
                if (userRes.code === 0 && userRes.data) {
                  const userDevices = Array.isArray(userRes.data) ? userRes.data : 
                                     Array.isArray(userRes.data.records) ? userRes.data.records : 
                                     Array.isArray(userRes.data.list) ? userRes.data.list : [];
                  
                  // 筛选出没有 stripeSubscriptionId 的设备
                  const unsubscribedDevices = userDevices.filter(
                    device => device && (!device.stripeSubscriptionId || device.stripeSubscriptionId === '' || device.stripeSubscriptionId === null)
                  );
                  
                  if (unsubscribedDevices.length > 0) {
                    data.unsubscribedDevices = unsubscribedDevices;
                    // 尝试打开弹窗
                    this.tryOpenExpireDialog(data);
                  }
                }
              }).catch(() => {
                // 如果获取用户设备列表失败，仍然尝试打开弹窗（可能只有过期设备）
                this.tryOpenExpireDialog(data);
              });
            } else {
              // 尝试打开弹窗：传入原始 data，支持后端多种字段（list/devices/equipmentList 等）
              // 同时在弹窗逻辑内兼容仅返回数量的情况
              this.tryOpenExpireDialog(data);
            }
          }
        });
      },
      isHeader() {
        if (document.body.clientWidth > 1200) {
          this.$store.dispatch("collapse", !this.$store.state.collapse);
        } else {
          this.$message.warning(this.$t("header.width"));
        }
      },
      toggleTheme() {
        this.$store.dispatch("setTheme", this.isDark ? THEMES.LIGHT : THEMES.DARK);
      },
      changeLang(lang) {
        this.$i18n.locale = lang;
        this.$cookies.set("lang", lang, { expires: 1095 });
      },
      userCommand(command) {
        if (command === "password") {
          this.handlePassword();
        } else if (command === "logout") {
          this.handleLoginOut();
        } else if (command === "wallet") {
          if (this.$route.path !== "/mywallet/topup") {
            this.$router.push("/mywallet/topup").catch(() => {});
          }
        } else if (command === "subscription") {
          this.manageSubscription();
        } else if (command === "account") {
          this.accountSettingsVisible = true;
        } else if (command === "notifications") {
          this.notifSettingsVisible = true;
        } else if (command === "classic") {
          this.openClassicSwitch();
        }
      },
      // 询问用户是否切换回经典版 UI，并收集可选反馈
      openClassicSwitch() {
        this.$prompt(this.$t("beta.switchHint"), this.$t("beta.switchTitle"), {
          confirmButtonText: this.$t("beta.switchConfirm"),
          cancelButtonText: this.$t("common.cancel"),
          inputPlaceholder: this.$t("beta.switchPlaceholder"),
          inputType: "textarea",
          // 必填：之前 8 次切换里 7 次留空，只有次数没有原因
          inputValidator: value => (value && value.trim().length >= 3 ? true : this.$t("beta.switchReasonRequired")),
          closeOnClickModal: false
        })
          .then(({ value }) => this.doSwitchClassic(value))
          .catch(() => {});
      },
      // 执行切换：上报 Intercom 事件与可选反馈，写入 cookie，跳转回当前页触发经典版渲染
      doSwitchClassic(reason) {
        try {
          if (window.Intercom) {
            window.Intercom("trackEvent", "beta_opt_out", { reason: reason || "" });
            if (reason) {
              window.Intercom("showNewMessage", "Beta feedback (switched to classic): " + reason);
            }
          }
        } catch (e) {
          /* ignore */
        }
        // 落库记录切换原因，便于统计（Intercom 事件不便查询，且非线上域名会被拒）
        // 失败不阻断切换：切 UI 是用户的诉求，反馈只是附带
        this.$api
          .request(this.$url.UiSwitchFeedback.insert, this.$method.post, {
            reason: reason || "",
            direction: "beta_to_classic",
            route: this.$route && this.$route.fullPath ? this.$route.fullPath : ""
          })
          .catch(() => {});

        this.$cookies.set("ui_pref", "classic", { expires: 180, path: "/" });
        // 开发环境只运行新版（无经典构建可跳），明确提示避免“无反应”困惑
        if (process.env.NODE_ENV === "development") {
          this.$message.info("Dev server runs the new UI only — the classic switch takes effect in the deployed (dual-build) environment. Preference saved.");
          return;
        }
        const target = this.$route && this.$route.fullPath ? this.$route.fullPath : "/main";
        setTimeout(() => {
          window.location.href = target;
        }, reason ? 800 : 0);
      },
      // 钱包余额（用于账户菜单展示）；无钱包授权的账号不请求
      loadWalletBalance() {
        if (!this.canWallet) {
          return;
        }
        this.$api
          .request(this.$url.myWallet.wallet, this.$method.get)
          .then(res => {
            if (res.code === 0 && res.data) {
              this.walletBalance = res.data.price;
            }
          })
          .catch(() => {});
      },
      passwordDialog(password) {
        this.dialogPassword = password;
      },
      password(password) {
        this.dialogPassword = password;
      },

      handlePassword() {
        this.$emit("password", true);
      },
      handleLoginOut() {
        this.$confirm(this.$t("header.isLoginOut"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          if (window.Intercom) {
            window.Intercom("shutdown");
          }
          this.$store.dispatch("loginOut");
          this.$router.push("/login");
        });
      },
      commandUser(command) {
        switch (command) {
          case "personal":
            this.$router.push("/user/info");
            break;
          case "password":
            this.$emit("password", true);
            break;
          case "loginOut":
            this.$confirm(this.$t("header.isLoginOut"), this.$t("common.tip"), {
              confirmButtonText: this.$t("common.definite"),
              cancelButtonText: this.$t("common.cancel"),
              type: "warning"
            }).then(() => {
              this.$store.dispatch("loginOut");
              this.$router.push("/login");
            });
            break;
        }
      },
      notice() {
        this.$message.warning(this.$t("common.await"));
        //this.$router.push('/user/message')
      },
      isDrawer() {
        this.$message.warning(this.$t("common.await"));
      },
      screen() {
        if (screenfull.isEnabled) {
          let that = this;
          screenfull.toggle().then(() => {
            that.$store.commit("INNER_HEIGHT", document.documentElement.clientHeight);
          });
        } else {
          this.$message.warning(this.$t("header.notScreenfull"));
        }
      },
      isTags() {
        this.$store.dispatch("tagsTop", !this.$store.state.tagsTop);
      },

      getText(expiredCount, aboutCount) {
        let text = "";

        // 处理已过期设备（使用 yOut：N台设备已到期）
        if (Array.isArray(expiredCount) && expiredCount.length > 0) {
          text += this.$t("header.yOut").replace("N", expiredCount.length);
        } else if (typeof expiredCount === "number" && expiredCount > 0) {
          text += this.$t("header.yOut").replace("N", expiredCount);
        }

        // 处理即将过期设备（使用 nOut：N台设备即将到期）
        if (Array.isArray(aboutCount) && aboutCount.length > 0) {
          text += text ? "，" : "";
          text += this.$t("header.nOut").replace("N", aboutCount.length);
        } else if (typeof aboutCount === "number" && aboutCount > 0) {
          text += text ? "，" : "";
          text += this.$t("header.nOut").replace("N", aboutCount);
        }

        return text;
      },

      // 安全解析日期字符串，无法解析返回 null
      parseExpireDate(dateStr) {
        if (!dateStr) return null;
        try {
          const d = new Date(dateStr);
          return isNaN(d.getTime()) ? null : d;
        } catch (e) {
          return null;
        }
      },

      // 根据 expireDate 对设备按“已过期”和“30天内即将过期”分类
      classifyDevicesByExpireDate(devices, days = 30) {
        const now = new Date().getTime();
        const limitMs = days * 24 * 60 * 60 * 1000;
        const expired = [];
        const aboutExpire = [];

        if (!Array.isArray(devices) || devices.length === 0) {
          return { expired, aboutExpire };
        }

        devices.forEach(item => {
          const d = this.parseExpireDate(item && item.expireDate);
          if (!d) return; // 没有有效时间则跳过，不参与分类
          const diff = d.getTime() - now;
          if (diff < 0) {
            expired.push(item);
          } else if (diff <= limitMs) {
            aboutExpire.push(item);
          }
        });

        return { expired, aboutExpire };
      },

      // 远期占位日期 = 未订阅（无到期）
      isNoExpirySentinel(dateStr) {
        if (!dateStr) return false;
        const d = this.parseExpireDate(dateStr);
        return null !== d && d.getFullYear() >= 2090;
      },

      // 格式化过期时间
      formatExpireDate(dateStr) {
        if (!dateStr) return "-";
        if (this.isNoExpirySentinel(dateStr)) return "—";
        try {
          const date = new Date(dateStr);
          return (
            date.getFullYear() +
            "-" +
            String(date.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(date.getDate()).padStart(2, "0") +
            " " +
            String(date.getHours()).padStart(2, "0") +
            ":" +
            String(date.getMinutes()).padStart(2, "0")
          );
        } catch (error) {
          return dateStr;
        }
      },

      // 获取设备状态文本
      getDeviceStatusText(device) {
        if (!device || !device.expireDate) {
          return "On Trial";
        }
        if (this.isNoExpirySentinel(device.expireDate)) {
          return "Not subscribed";
        }
        const expireDate = new Date(device.expireDate);
        const now = new Date();
        return expireDate > now ? "On Trial" : "Expired";
      },

      // 获取设备状态类型（用于标签颜色）
      getDeviceStatusType(device) {
        if (!device || !device.expireDate) {
          return "success";
        }
        if (this.isNoExpirySentinel(device.expireDate)) {
          return "info";
        }
        const expireDate = new Date(device.expireDate);
        const now = new Date();
        return expireDate > now ? "success" : "danger";
      },

      // 查看设备详情
      viewDevice(device) {
        this.$router.push({
          path: "/machine/user/machine-user",
          query: {
            equipmentNo: device.equipmentNo,
            autoSearch: true
          }
        });
      },

      // 处理已过期设备选择变化
      // 已移除勾选功能，方法保留为空以兼容旧代码（不再使用）
      handleExpiredSelectionChange() {},

      // 处理即将过期设备选择变化
      // 已移除勾选功能，方法保留为空以兼容旧代码（不再使用）
      handleAboutExpireSelectionChange() {},
      onNewDeviceSelectionChange(selection) {
        this.selectedNewDevices = selection;
      },
      onOldDeviceSelectionChange(selection) {
        this.selectedOldDevices = selection;
      },

      // 批量续费
      async batchRenewal() {
        const allDevices = [...this.selectedNewDevices, ...this.selectedOldDevices];
        if (allDevices.length === 0) {
          this.$message.warning(this.$t("header.pleaseSelectDeviceToRenew"));
          return;
        }
        
        // Check payment method status first
        try {
          const res = await this.$api.request(this.$url.Stripe.checkPayment, this.$method.get, {});
          if (res && res.code === 0) {
            const state = res.data && typeof res.data.state === "number" ? res.data.state : 0;
            // state === 1 means no payment method
            if (state === 1) {
              // Show welcome guide
              this.showWelcomeDialog = true;
              this.hasPaymentMethod = false;
              return;
            }
          }
        } catch (error) {
          // If check fails, proceed anyway
          console.error('Payment check failed:', error);
        }
        
        // Has payment method, proceed to plan selection
        this.renewalList = allDevices;
        this.dialogBatchRenewal = true;
      },

      // Redirect to Stripe portal for payment setup
      async redirectToStripePortal() {
        const newWin = window.open('', '_blank');
        try {
          const portal = await this.$api.request(this.$url.Stripe.machineGetUrl, this.$method.get, {});
          const url = portal?.data?.url || portal?.data?.href || portal?.data;
          if (portal.code === 0 && url) {
            if (newWin) {
              newWin.location.href = url;
            } else {
              window.location.href = url;
            }
            return;
          }
        } catch (e) {
          // ignore
        }

        try {
          const addRes = await this.$api.request(this.$url.Stripe.add, this.$method.get, {});
          const addUrl = addRes?.data?.url || addRes?.data?.href || addRes?.data;
          if (addRes.code === 0 && addUrl) {
            if (newWin) {
              newWin.location.href = addUrl;
            } else {
              window.location.href = addUrl;
            }
            return;
          }
        } catch (e) {
          // ignore
        }

        if (newWin && newWin.close) {
          try { newWin.close(); } catch (_) {}
        }
        this.$message.warning(this.$t('subscribe.messages.cannotJumpCardPortal') || 'Unable to open payment setup');
      },

      // 弹窗允许用户随时关闭
      onExpireDialogBeforeClose(done) {
        try { done && done(); } catch (_) {}
        this.allowExpireDialogClose = false;
      },

      // 跳转到订阅管理
      goToManageSubscription(machine) {
        if (machine) {
          this.renewalList = [machine];
        } else {
          this.renewalList = this.noticeDevices;
        }
        this.dialogBatchRenewal = true;
        // this.expireDialogVisible = false;
        // this.$router.push({
        //   path: "/machine/user/machine-user",
        //   query: {
        //     renewalStatus: -1
        //   }
        // });
      },
      // 统一的弹窗开启逻辑（支持登录后 Store 更新或本组件主动拉取）
      tryOpenExpireDialog(data) {
        // 若隐私协议弹窗尚未完成，直接不打开设备过期弹窗
        if (this.$store && this.$store.state && this.$store.state.privacyVisible) {
          this.expireDialogVisible = false;
          return;
        }
        // 兼容接口返回的不同字段命名，统一取设备数组
        const expiredList =
          data && Array.isArray(data.expiredDevices)
            ? data.expiredDevices
            : data && Array.isArray(data.expired)
            ? data.expired
            : [];
        const aboutList =
          data && Array.isArray(data.aboutExpireDevices)
            ? data.aboutExpireDevices
            : data && Array.isArray(data.aboutDevices)
            ? data.aboutDevices
            : data && Array.isArray(data.about)
            ? data.about
            : [];
        // 检查未订阅设备
        const unsubscribedList =
          data && Array.isArray(data.unsubscribedDevices)
            ? data.unsubscribedDevices
            : data && Array.isArray(data.unsubscribed)
            ? data.unsubscribed
            : [];
        // 兼容接口返回统一列表字段或直接返回数组
        const unifiedList = Array.isArray(data)
          ? data
          : data && Array.isArray(data.list)
          ? data.list
          : data && Array.isArray(data.devices)
          ? data.devices
          : data && Array.isArray(data.equipmentList)
          ? data.equipmentList
          : [];
        // 兼容仅返回数量的情况（expiredCount/expired、aboutCount/about 为 number）
        const expiredNum = Array.isArray(expiredList)
          ? expiredList.length
          : data && typeof data.expiredCount === "number"
          ? data.expiredCount
          : data && typeof data.expired === "number"
          ? data.expired
          : 0;
        const aboutNum = Array.isArray(aboutList)
          ? aboutList.length
          : data && typeof data.aboutCount === "number"
          ? data.aboutCount
          : data && typeof data.about === "number"
          ? data.about
          : 0;
        const unsubscribedNum = Array.isArray(unsubscribedList)
          ? unsubscribedList.length
          : data && typeof data.unsubscribedCount === "number"
          ? data.unsubscribedCount
          : 0;
        // 合并三类设备列表为一个统一列表（不区分到期/即将到期/未订阅），并去重
        const map = new Map();
        const pushUnique = item => {
          if (!item) return;
          const key = item.equipmentNo || JSON.stringify(item);
          if (!map.has(key)) map.set(key, item);
        };
        expiredList.forEach(pushUnique);
        aboutList.forEach(pushUnique);
        unsubscribedList.forEach(pushUnique);
        unifiedList.forEach(pushUnique);
        const allDevices = Array.from(map.values());

        // 填充统一列表，并清空旧分类列表
        this.noticeDevices = allDevices;
        // 根据 isTransferNew 分成两个列表：1 新设备，0 旧设备（其他或未定义按旧设备处理）
        this.newDevices = allDevices.filter(item => item && item.isTransferNew == 1);
        this.oldDevices = allDevices.filter(item => !item || item.isTransferNew != 1);
        this.expiredDevices = [];
        this.aboutExpireDevices = [];
        this.selectedExpiredDevices = [];
        this.selectedNewDevices = [];
        this.selectedOldDevices = [];
        this.selectedAboutExpireDevices = [];

        // 只要接口返回了设备数组或数量为正（包括未订阅设备），就弹窗并展示；
        // 若本次没有数据但此前已有列表，避免被其他更新关闭弹窗
        const totalCount = allDevices.length > 0 ? allDevices.length : expiredNum + aboutNum + unsubscribedNum;
        if (allDevices.length > 0 || expiredNum + aboutNum + unsubscribedNum > 0) {
          const total = allDevices.length > 0 ? allDevices.length : expiredNum + aboutNum + unsubscribedNum;
          // 国际化：使用两个键 header.expireSummaryTitle 与 header.expireSummaryDesc
          this.expireMessageTitle = this.$t("header.expireSummaryTitle", { count: total });
          this.expireMessageDesc = this.$t("header.expireSummaryDesc", { count: total });
          this.expireMessage = this.$t("header.expireSummary", { count: total });
          this.expireDialogVisible = true;
        } else if (Array.isArray(this.noticeDevices) && this.noticeDevices.length > 0) {
          this.expireDialogVisible = true;
        } else {
          this.expireMessage = "";
          this.expireMessageTitle = "";
          this.expireMessageDesc = "";
          this.expireDialogVisible = false;
        }
      }
    },
    computed: {
      // 钱包入口仅对已授权 /mywallet 页面的账号展示（子账号菜单可能没有钱包）
      canWallet() {
        return grantedLinks(this.$store.state.menuList).has("/mywallet/topup");
      },
      // 为了更稳定地监听 Store 中的 machineInfo 变化，增加一个计算属性供 watch 使用
      storeMachineInfo() {
        return this.$store.state.machineInfo;
      },
      collapse() {
        return this.$store.state.collapse;
      },
      isDark() {
        return this.$store.state.theme === THEMES.DARK;
      },
      lang() {
        return this.$i18n.locale;
      },
      pageTitle() {
        return (this.$route.meta && this.$route.meta.title) || this.$route.name || "";
      },
      userAvatar() {
        return this.$store.state.avatar;
      },
      nickName() {
        return this.$store.state.nickName;
      },
      isDot() {
        return this.$store.state.isDot;
      },
      machineInfo() {
        const info = this.$store.state.machineInfo || {};
        const expired = Array.isArray(info.expiredDevices)
          ? info.expiredDevices
          : Array.isArray(info.expiredCount)
          ? info.expiredCount
          : info.expired;
        const about = Array.isArray(info.aboutExpireDevices)
          ? info.aboutExpireDevices
          : Array.isArray(info.aboutDevices)
          ? info.aboutDevices
          : Array.isArray(info.aboutCount)
          ? info.aboutCount
          : info.about;
        return this.getText(expired, about);
      },
      // 计算总设备数量（已过期 + 即将过期）
      totalDevicesCount() {
        return this.noticeDevices.length;
      },
      selectedDevicesCount() {
        return this.selectedNewDevices.length + this.selectedOldDevices.length;
      }
    },
    beforeDestroy() {
      eventBus.off("openPasswordDialog", this.handlePassword);
    },
    watch: {
      // 登录后 Store 中的 machineInfo 更新时，自动尝试打开过期提醒弹窗
      storeMachineInfo: {
        // 不再通过 Store 的数字更新触发弹窗开关，避免覆盖接口设备数据导致弹窗被关闭
        immediate: false,
        handler(info) {
          // 可选：仅更新提示文案，不改变弹窗显隐
          // const expired = typeof info.expired === 'number' ? info.expired : 0;
          // const about = typeof info.about === 'number' ? info.about : 0;
          // this.expireMessage = this.getText(expired, about);
        }
      }
    }
  };
</script>

<style lang="scss">
  /* 订阅引导弹窗：颜色从行内样式挪出来，暗色下跟随令牌 */
  .vtm-welcome-sub__title { color: #333; }
  .vtm-welcome-sub__text { color: #666; }
  .vtm-welcome-sub__step { background: #f8f9fc; }
  [data-theme="dark"] {
    .vtm-welcome-sub__title { color: var(--vtm-heading); }
    .vtm-welcome-sub__text { color: var(--vtm-ink-2); }
    .vtm-welcome-sub__step { background: var(--vtm-well); }
  }
  /* 账户下拉菜单：钱包余额行 */
  .vtm-account-menu__balance {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    column-gap: 10px;
    row-gap: 2px;
    padding: 8px 20px 10px;
  }
  /* 余额行内的充值按钮（替代左侧导航的充值页链接） */
  .vtm-account-menu__topup {
    grid-row: 1 / span 2;
    grid-column: 2;
    padding: 5px 12px;
  }
  .vtm-account-menu__balance-label {
    font-size: 11px;
    color: var(--vtm-ink-3);
  }
  .vtm-account-menu__balance-val {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .vtm-header {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 56px;
    flex-shrink: 0;
    padding: 0 16px 0 10px;
    background: var(--vtm-surface);
    border-bottom: 1px solid var(--vtm-line);
  }

  .vtm-header__iconbtn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-2);
    font-size: 17px;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
      color: var(--vtm-heading);
    }

    &:focus-visible {
      outline: 2px solid var(--vtm-mint-dim);
      outline-offset: 1px;
    }

    .el-badge {
      line-height: 1;
    }
  }

  .vtm-header__textbtn {
    border: none;
    padding: 7px 12px;
    border-radius: var(--vtm-r-sm);
    background: transparent;
    color: var(--vtm-ink-2);
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
      color: var(--vtm-heading);
    }
  }

  .vtm-header__lang {
    font-size: 12.5px;
    font-weight: 700;
    font-family: var(--vtm-body);
  }

  .vtm-header__title {
    margin-left: 6px;
    font-family: var(--vtm-display);
    font-size: 15px;
    font-weight: 600;
    color: var(--vtm-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .vtm-beta-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 7px;
    border-radius: 10px;
    background: var(--vtm-mint-soft);
    color: var(--vtm-mint-ink);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .vtm-header__spacer {
    flex: 1;
  }

  .vtm-header__user {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px 4px 4px;
    margin-left: 4px;
    border: none;
    border-radius: 20px;
    background: transparent;
    cursor: pointer;
    transition: background-color var(--vtm-speed) ease;

    &:hover {
      background: var(--vtm-well);
    }

    &:focus-visible {
      outline: 2px solid var(--vtm-mint-dim);
      outline-offset: 1px;
    }
  }

  .vtm-header__avatar {
    background: var(--vtm-navy);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
  }

  [data-theme="dark"] .vtm-header__avatar {
    background: var(--vtm-mint);
    color: var(--vtm-navy);
  }

  .vtm-header__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .vtm-header__caret {
    font-size: 11px;
    color: var(--vtm-ink-3);
  }

  @media (max-width: 640px) {
    .vtm-header__name {
      display: none;
    }
  }

  .msg-popover {
    .msg-content {
      height: 280px;
      .msg {
        background: var(--vtm-well);
        border-radius: var(--vtm-r-sm);
        padding: 10px;
        cursor: pointer;
        margin-bottom: 5px;
        color: var(--vtm-ink);
      }
      .msg:last-child {
        margin-bottom: 0;
      }
      .no-more {
        text-align: center;
      }
    }
  }

  /* Custom expire alert styling */
  .custom-expire-alert {
    &.el-alert--warning {
      background-color: var(--vtm-crit-soft) !important;
      border-color: var(--vtm-crit-soft) !important;

      .el-alert__title,
      .el-alert__description {
        color: var(--vtm-ink) !important;
      }

      .el-alert__icon {
        color: var(--vtm-crit) !important;
      }
    }
  }
</style>
